import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { doFetch, registerTeamsChannel } from '../services/api';
import { environment } from '../../environments/environment';
import { app } from '@microsoft/teams-js';
import { setWorkspaceTimezone } from '../../lib/cube-helper';

// Type definitions for Microsoft Teams context (matches API /teams/authorize body)
interface TeamsUser {
  id?: string;
  displayName?: string;
  licenseType?: string;
  loginHint?: string;
  userPrincipalName?: string;
  tenant?: {
    id?: string;
    teamsSku?: string;
  };
}

/** Microsoft Teams SDK context shape */
interface TeamsSDKContext {
  user?: TeamsUser;
  app?: {
    locale?: string;
    theme?: string;
  };
  channel?: {
    id?: string;
    displayName?: string;
  };
  team?: {
    id?: string;
    displayName?: string;
  };
}

/** From registerTeamsChannel successful response */
export interface AgentDetails {
  /** Cube Agent.id â€” never use this as the AI agent UUID. */
  agentUserId: string;
  agentName: string;
  /** AI agent identity used to match fetchAgents / dropdown rows. */
  agentUUID: string;
  version?: string;
}

export function getAgentDetailKey(detail: AgentDetails): string {
  return `${detail.agentUUID}:${detail.version ?? ''}`;
}

interface UseTeamsContextResult {
  context: TeamsSDKContext | null;
  user: TeamsUser | null;
  loading: boolean;
  error: Error | null;
  initialized: boolean;
  /** JWT from /teams/authorize for Cube.js analytics API */
  cubeJsToken: string | null;
  /** JWT from /teams/authorize for REST API calls */
  authToken: string | null;
  /** App ID from /teams/authorize for API paths */
  appId: string | null;
  /** Tenant ID from /teams/authorize response (API may return misspelled key) */
  tenantId: string | null;
  /** Workspace timezone from /teams/authorize when provided */
  timezone: string | null;
  /** First (or currently default) agent from registerTeamsChannel */
  agentDetails: AgentDetails | null;
  /** All agents linked to the Teams channel (powers the header switcher). */
  agentDetailsList: AgentDetails[];
  /** True once registerTeamsChannel has settled (or was skipped). */
  agentChannelResolved: boolean;
}

interface TeamsAuthorizeResponse {
  status?: string;
  message?: string;
  cubeJsToken?: string;
  authToken?: string;
  appId?: string | number;
  tenantId?: string;
  tentantId?: string;
  timezone?: string | null;
}

const defaultContextValue: UseTeamsContextResult = {
  context: null,
  user: null,
  loading: false,
  error: null,
  initialized: false,
  cubeJsToken: null,
  authToken: null,
  appId: null,
  tenantId: null,
  timezone: null,
  agentDetails: null,
  agentDetailsList: [],
  agentChannelResolved: false,
};

/** Mock Teams context for local development when isLocal is true */
const MOCK_TEAMS_CONTEXT = {"app":{"locale":"en-gb","sessionId":"5a1ad341-eafd-4c02-bcbb-179723212957","theme":"default","iconPositionVertical":-17,"osLocaleInfo":{"platform":"windows","regionalFormat":"en-GB","shortDate":"dd/MM/yyyy","longDate":"dd MMMM yyyy","shortTime":"HH:mm","longTime":"HH:mm:ss","timezone":"Asia/Calcutta"},"parentMessageId":"","userClickTime":1787834739264,"userClickTimeV2":0,"userFileOpenPreference":"inline","host":{"name":"TeamsModern","clientType":"web","features":{"nestedWildcardsInValidDomains":false,"serverSideRendering":false},"sessionId":"36d22e0c-9423-44f5-9cff-321942e69990","ringId":"general","ancestors":["TeamsModern"]},"appLaunchId":"b6aab36c-9b4d-42b8-a436-6564f6bef634","appId":{"appIdAsString":"2ed0fb23-32b9-477d-9a94-c242a0b3e23c"},"manifestVersion":"1.22"},"page":{"id":"agentSummaryTab","frameContext":"content","subPageId":"","isFullScreen":false,"isMultiWindow":false,"isBackgroundLoad":false,"sourceOrigin":""},"user":{"id":"43593a73-fbda-44c5-9a62-ad385dbd105a","displayName":"","licenseType":"SmbNonVoice","loginHint":"akshay@TheLoops365.onmicrosoft.com","userPrincipalName":"akshay@TheLoops365.onmicrosoft.com","tenant":{"id":"2b09fb12-7295-4d4c-9ed9-8052e677bc03","teamsSku":"enterprise"}},"channel":{"id":"19:xjsw_8IUD6t_0IcEWdMSgG1xOclGWuMdkYoWE081FyY1@thread.tacv2","displayName":"Change Requests","relativeUrl":"/sites/ITOperations/Shared Documents/Change Requests","membershipType":"Regular","defaultOneNoteSectionId":""},"team":{"internalId":"19:xjsw_8IUD6t_0IcEWdMSgG1xOclGWuMdkYoWE081FyY1@thread.tacv2","displayName":"IT Operations","type":0,"groupId":"dc1c5e81-e31d-4475-87c4-fd1966d84065","isArchived":false,"userRole":0},"sharePointSite":{"teamSiteUrl":"https://theloops365.sharepoint.com/sites/ITOperations","teamSiteDomain":"theloops365.sharepoint.com","teamSitePath":"/sites/ITOperations","teamSiteId":""},"dialogParameters":{}} as TeamsSDKContext;

const TeamsContext = createContext<UseTeamsContextResult>(defaultContextValue);

/**
 * Provider that initializes Teams context and authorize flow; must wrap the app so useTeamsContext() has access to cubeJsToken and user.
 */
export function TeamsProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<TeamsSDKContext | null>(null);
  const [user, setUser] = useState<TeamsUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [cubeJsToken, setCubeJsToken] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [agentDetails, setAgentDetails] = useState<AgentDetails | null>(null);
  const [agentDetailsList, setAgentDetailsList] = useState<AgentDetails[]>([]);
  const [agentChannelResolved, setAgentChannelResolved] = useState<boolean>(false);
  const getClientTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  };

  const applyAuthorizeResponse = (
    res: TeamsAuthorizeResponse,
    teamsContext: TeamsSDKContext,
    options?: { requireAgentOnChannel?: boolean }
  ) => {
    if (res.status !== 'ok') {
      setError(new Error(res.message ?? 'Teams authorize returned non-ok status'));
      return;
    }

    const cubeToken = res.cubeJsToken;
    const auth = res.authToken;
    const appIdFromRes = res.appId;
    const tenantFromRes = res.tenantId ?? res.tentantId;
    const tenantFromTeams = teamsContext.user?.tenant?.id;

    const resolvedTenantId = String(tenantFromRes ?? tenantFromTeams ?? '') || null;
    setTenantId(resolvedTenantId);
    if (resolvedTenantId) {
      localStorage.setItem('tenant', resolvedTenantId);
    }

    if (res.timezone) {
      setTimezone(res.timezone);
      setWorkspaceTimezone(res.timezone);
    } else {
      const clientTimezone = getClientTimezone();
      setTimezone(clientTimezone);
      setWorkspaceTimezone(clientTimezone);
    }

    if (cubeToken) {
      setCubeJsToken(cubeToken);
      localStorage.setItem('cubejsToken', cubeToken);
    } else {
      setError(new Error('Missing cubeJsToken from Teams authorize response'));
    }

    if (auth) {
      setAuthToken(auth);
      localStorage.setItem('authToken', auth);
      const tenantIdForChannel = teamsContext.user?.tenant?.id;
      const channelId = teamsContext.channel?.id;
      const teamId = (teamsContext as any)?.team?.groupId as string | undefined;
      if (tenantIdForChannel && channelId) {
        registerTeamsChannel(tenantIdForChannel, channelId, auth, teamId, environment.envName)
          .then(
            (channelRes) => {
              if (channelRes == null) return;
              const wrapped = channelRes as {
                data?: {
                  agentDetails?: Array<{
                    agentUserId?: string | number;
                    agentName?: string;
                    agentUUID?: string;
                    version?: string;
                  }>;
                };
                agentDetails?: Array<{
                  agentUserId?: string | number;
                  agentName?: string;
                  agentUUID?: string;
                  version?: string;
                }>;
              };
              const agentDetailsArr = wrapped?.data?.agentDetails ?? wrapped?.agentDetails;
              if (Array.isArray(agentDetailsArr) && agentDetailsArr.length > 0) {
                const normalized: AgentDetails[] = agentDetailsArr.map((item) => ({
                  agentUserId: String(item.agentUserId ?? ''),
                  agentName: item.agentName ?? '',
                  agentUUID: item.agentUUID ?? '',
                  version: item.version,
                }));
                setAgentDetailsList(normalized);
                setAgentDetails(normalized[0]);
              } else if (options?.requireAgentOnChannel) {
                setError(
                  new Error(
                    'No agent is linked to this channel yet. Please register an agent to continue.'
                  )
                );
              }
            },
            (err) => console.warn('Teams channel registration failed:', err)
          )
          .finally(() => setAgentChannelResolved(true));
      } else {
        setAgentChannelResolved(true);
      }
    }

    if (appIdFromRes) {
      setAppId(String(appIdFromRes));
    }
  };

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Tab configuration is handled in index.html; skip SDK work in that iframe.
        if (typeof window !== 'undefined' && window.__TEAMS_SETTINGS_FRAME) {
          setLoading(false);
          setInitialized(true);
          setAgentChannelResolved(true);
          return;
        }

        // Local development: use mocked Teams context (no SDK required)
        if (environment.isLocal) {
          console.log('isLocal: true - using mocked Teams context for local development');
          const teamsContext = MOCK_TEAMS_CONTEXT;
          setContext(teamsContext);
          setUser(teamsContext.user || null);
          setInitialized(true);
          setLoading(false);

          if (teamsContext) {
            doFetch('api/teams/authorize', {
              method: 'POST',
              body: teamsContext,
              skipAuth: true,
            })
              .then((result) => {
                if (result != null) {
                  console.log('Teams authorize success (mock):', result);
                  applyAuthorizeResponse(result as TeamsAuthorizeResponse, teamsContext);
                } else {
                  setError(new Error('Empty response from Teams authorize'));
                }
              })
              .catch((err) => {
                console.error('Teams authorize error:', err);
                const authError = err instanceof Error ? err : new Error(String(err));
                setError(authError);
              });
          }
          return;
        }

        // Use official Teams v2 SDK import instead of window.microsoftTeams
        await app.initialize();
        setInitialized(true);

        const teamsContext = await app.getContext();
        console.log('Full Teams Context:', teamsContext);
        console.log('User Info:', teamsContext.user);
        console.log('User JSON:', JSON.stringify(teamsContext.user, null, 2));

        const frameContext =
          (teamsContext as { page?: { frameContext?: string }; frameContext?: string })?.page?.frameContext ||
          (teamsContext as { frameContext?: string }).frameContext;
        if (frameContext === 'settings') {
          window.__TEAMS_SETTINGS_FRAME = true;
          setLoading(false);
          setInitialized(true);
          setAgentChannelResolved(true);
          return;
        }

        setContext(teamsContext as unknown as TeamsSDKContext);
        setUser((teamsContext as any).user || null);
        setLoading(false);

        if (teamsContext) {
          doFetch('api/teams/authorize', {
            method: 'POST',
            body: teamsContext,
            skipAuth: true,
          })
            .then((result) => {
              if (result != null) {
                console.log('Teams authorize success:', result);
                applyAuthorizeResponse(result as TeamsAuthorizeResponse, teamsContext as TeamsSDKContext, {
                  requireAgentOnChannel: true,
                });
              } else {
                setError(new Error('Empty response from Teams authorize'));
              }
            })
            .catch((err) => {
              console.error('Teams authorize error:', err);
              const authError = err instanceof Error ? err : new Error(String(err));
              setError(authError);
            });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        const msg = (error.message || '').toLowerCase();
        const asString = String(err).toLowerCase();

        if (
          msg.includes('no parent window') ||
          msg.includes('initialization failed') ||
          msg.includes('not in teams') ||
          asString.includes('no parent window') ||
          asString.includes('initialization failed')
        ) {
          console.log('Not running in Microsoft Teams environment');
          setLoading(false);
          setInitialized(false);
          return;
        }

        console.error('Error initializing Teams context:', error);
        setError(error);
        setLoading(false);
        setInitialized(false);
      }
    };

    initializeTeams().catch((err) => {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Unhandled error initializing Teams context:', error);
      setError(error);
      setLoading(false);
      setInitialized(false);
    });
  }, []);

  // Store tokens in localStorage when available (for cube-helper and other consumers)
  useEffect(() => {
    if (cubeJsToken) {
      localStorage.setItem('cubejsToken', cubeJsToken);
    }
  }, [cubeJsToken]);
  useEffect(() => {
    if (authToken) {
      localStorage.setItem('authToken', authToken);
    }
  }, [authToken]);
  useEffect(() => {
    if (tenantId) {
      localStorage.setItem('tenant', tenantId);
    }
  }, [tenantId]);

  const value: UseTeamsContextResult = {
    context,
    user,
    loading,
    error,
    initialized,
    cubeJsToken,
    authToken,
    appId,
    tenantId,
    timezone,
    agentDetails,
    agentDetailsList,
    agentChannelResolved,
  };

  return (
    <TeamsContext.Provider value={value}>
      {children}
    </TeamsContext.Provider>
  );
}

/**
 * React hook to access Microsoft Teams context, user, and cubeJsToken.
 * Must be used within TeamsProvider.
 *
 * @returns {UseTeamsContextResult} context, user, loading, error, initialized, cubeJsToken
 */
export function useTeamsContext(): UseTeamsContextResult {
  return useContext(TeamsContext);
}